import type { SystemLogRequest, PerformanceMetric } from "./microservice";

export const MONITORING_HEALTH_RETRY_MS = 300000;
export const MONITORING_LOG_TIMEOUT_MS = 3000;

export function shouldRetryMonitoringAfterOutage(
  serviceAvailable: boolean | null,
  lastHealthCheck: number,
  now: number
): boolean {
  if (serviceAvailable !== false) {
    return true;
  }

  return now - lastHealthCheck > MONITORING_HEALTH_RETRY_MS;
}

export function isCriticalLogLevel(level: SystemLogRequest["level"] | "fatal"): boolean {
  return level === "error" || level === "fatal" || level === "warn";
}

export function shouldGateNonCriticalAdminLogging(
  isCriticalError: boolean,
  userId: string | undefined
): boolean {
  return !isCriticalError && Boolean(userId);
}

/**
 * Build System Log Payload.
 */
export function buildSystemLogPayload(
  logData: SystemLogRequest,
  userId: string | undefined,
  userEmail: string | undefined
) {
  return {
    level: logData.level || "info",
    component: logData.component || "system",
    message: logData.message || "",
    service: logData.service || "frontend",
    userId: userId || logData.user_id || null,
    userEmail: userEmail || null,
    details: logData.metadata || {},
    context: logData.metadata || {},
  };
}

export function buildSystemLogsQueryParams(options: {
  limit?: number;
  level?: string;
  component?: string;
}): URLSearchParams {
  const params = new URLSearchParams();
  if (options.limit) params.append("limit", options.limit.toString());
  if (options.level) params.append("level", options.level);
  if (options.component) params.append("component", options.component);
  return params;
}

/**
 * Map System Log Record.
 */
export function mapSystemLogRecord(log: Record<string, unknown>) {
  return {
    id: log.id || log.uuid || `log-${Date.now()}`,
    level: log.level || "info",
    component: log.component || "system",
    message: log.message || "",
    service: log.service || undefined,
    user_id: log.user_id || log.userId || undefined,
    metadata: log.details || log.context || log.metadata || {},
    timestamp: log.timestamp || log.created_at || new Date().toISOString(),
    created_at: log.created_at || log.timestamp || new Date().toISOString(),
  };
}

/**
 * Build Performance Metric Payload.
 */
export function buildPerformanceMetricPayload(metric: PerformanceMetric) {
  return {
    serviceName: metric.service_name || "frontend",
    metricName: metric.metric_name || "unknown",
    cpu: 0,
    memoryUsed: 0,
    memoryTotal: 0,
    apiResponseTime: metric.metric_value || 0,
    requestsPerMinute: 0,
    activeConnections: 0,
    databaseConnections: 0,
    databaseQueryTime: 0,
    cacheHitRatio: 0,
    webhookResponseTime: 0,
    queueLength: 0,
    errorRate: 0,
  };
}

export function buildPerformanceMetricsQueryParams(options: {
  limit?: number;
  timeRange?: "1h" | "24h" | "7d" | "30d";
}): URLSearchParams {
  const params = new URLSearchParams();
  if (options.limit) params.append("limit", options.limit.toString());
  if (options.timeRange) params.append("timeRange", options.timeRange);
  return params;
}

export function mapPerformanceMetricRecord(metric: Record<string, unknown>) {
  return {
    id: metric.id || `metric-${Date.now()}`,
    metric_name: metric.metric_name || "unknown",
    metric_value: metric.metric_value || metric.api_response_time || 0,
    service_name: metric.service_name || "unknown",
    timestamp: metric.timestamp || metric.created_at || new Date().toISOString(),
    created_at: metric.created_at || metric.timestamp || new Date().toISOString(),
  };
}

export function extractCollectionFromApiResult<T>(result: unknown): T[] {
  return Array.isArray(result) ? result : ((result as { data?: T[] }).data ?? []);
}

/**
 * Fetch Mapped System Logs.
 */
export async function fetchMappedSystemLogs(options: {
  logsUrl: string;
  accessToken: string;
  query: {
    limit?: number;
    level?: string;
    component?: string;
  };
}): Promise<
  { success: true; data: ReturnType<typeof mapSystemLogRecord>[] } | { success: false; data: [] }
> {
  const params = buildSystemLogsQueryParams(options.query);
  const response = await fetch(`${options.logsUrl}?${params.toString()}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${options.accessToken}`,
      "Content-Type": "application/json",
    },
    credentials: "include",
  });

  const result = await response.json();

  if (!response.ok) {
    console.warn("Failed to fetch system logs:", result);
    return { success: false, data: [] };
  }

  const logs = extractCollectionFromApiResult<Record<string, unknown>>(result);
  return {
    success: true,
    data: logs.map((log) => mapSystemLogRecord(log)),
  };
}
